import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import {
    CheckCircleOutline as CheckIcon,
    ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { Modal } from '../../../../shared/ui/Modal';

type SpreadsheetUploadValidationModalProps = {
    open: boolean;
    onClose: () => void;
    missingColumns: string[];
    fileName?: string;
    sheetName?: string;
    dataValidationMessage?: string;
    warningMessage?: string;
};

export const SpreadsheetUploadValidationModal: React.FC<
    SpreadsheetUploadValidationModalProps
> = ({
    open,
    onClose,
    missingColumns,
    fileName,
    sheetName,
    dataValidationMessage,
    warningMessage,
}) => {
        const isValid = missingColumns.length === 0;
        const hasDataIssues = !!`${dataValidationMessage ?? ''}`.trim();
        const hasWarnings = !!`${warningMessage ?? ''}`.trim();
        const showSuccess = isValid && !hasDataIssues;

        const title = !isValid
            ? 'Faltam colunas'
            : hasDataIssues
                ? 'Planilha com inconsistências'
                : hasWarnings
                    ? 'Tudo certo (com avisos)'
                    : 'Tudo certo';

        return (
            <Modal open={open} handleClose={onClose} width="520px">
                <Box>
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            justifyContent: 'center',
                        }}
                    >
                        {showSuccess ? (
                            <CheckIcon color="success" />
                        ) : (
                            <ErrorIcon color="error" />
                        )}
                        <Typography
                            variant="h5"
                            style={{ fontWeight: 700, textAlign: 'center' }}
                        >
                            {title}
                        </Typography>
                    </Box>

                    {(fileName || sheetName) && (
                        <Typography
                            variant="body2"
                            style={{ color: '#6b7280', textAlign: 'center', marginTop: 6 }}
                        >
                            {fileName ? `Arquivo: ${fileName}` : ''}
                            {fileName && sheetName ? ' | ' : ''}
                            {sheetName ? `Aba: ${sheetName}` : ''}
                        </Typography>
                    )}

                    <Box style={{ marginTop: 16 }}>
                        {isValid ? (
                            <Typography variant="body1" style={{ textAlign: 'center' }}>
                                Todas as colunas correspondentes foram encontradas.
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="body1" style={{ marginBottom: 10 }}>
                                    Colunas obrigatórias que não foram encontradas:
                                </Typography>
                                <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {missingColumns.map((c) => (
                                        <Chip key={c} label={c} color="error" variant="outlined" />
                                    ))}
                                </Box>
                            </>
                        )}
                    </Box>

                    {hasDataIssues ? (
                        <Box
                            style={{
                                marginTop: 14,
                                padding: 12,
                                border: '1px solid #fecaca',
                                background: '#fff7f7',
                                borderRadius: 10,
                            }}
                        >
                            <Typography
                                variant="body2"
                                style={{
                                    color: '#b42318',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.45,
                                }}
                            >
                                {dataValidationMessage}
                            </Typography>
                            <Typography
                                variant="body2"
                                style={{ color: '#6b7280', marginTop: 8 }}
                            >
                                O botão “Enviar” ficará desabilitado até corrigir a planilha.
                            </Typography>
                        </Box>
                    ) : null}

                    {showSuccess && hasWarnings ? (
                        <Box
                            style={{
                                marginTop: 14,
                                padding: 12,
                                border: '1px solid #fde68a',
                                background: '#fffbeb',
                                borderRadius: 10,
                            }}
                        >
                            <Typography
                                variant="body2"
                                style={{
                                    color: '#92400e',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.45,
                                }}
                            >
                                {warningMessage}
                            </Typography>
                        </Box>
                    ) : null}

                    <Box style={{ marginTop: 20, textAlign: 'center' }}>
                        <Button variant="contained" onClick={onClose}>
                            Ok
                        </Button>
                    </Box>
                </Box>
            </Modal>
        );
    };
